import React, { useEffect } from 'react';
import { 
    CheckCircle2, 
    AlertCircle, 
    Compass,
    Database,
    LineChart,
    Target,
    Filter,
    Workflow,
    ArrowRight
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-lg p-6 shadow-xl ${className}`}>
        {children}
    </div>
);

const CheckRow = ({ children }) => (
    <li className="flex gap-4 items-start">
        <CheckCircle2 className="w-6 h-6 mt-0.5 flex-shrink-0 text-indigo-400" aria-hidden />
        <span className="text-white/80 leading-relaxed text-lg">{children}</span>
    </li>
);

export default function ShreyaProposal() {
    useEffect(() => {
        document.title = 'Shreya Pattar | Growth Partner Proposal';
    }, []);

    return (
        <div className="min-h-screen bg-transparent font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24">
            
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                 <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                     <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                 </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-24">
                
                {/* Hero */}
                <section className="text-center pt-10">
                    <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        The Founder-Led Growth Engine
                    </h1>
                    <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                        Transitioning your audience from freelancers to founders, and transforming your content into a predictable lead-flow machine.
                    </p>
                </section>

                {/* The Problem */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-orange-400 mb-4 flex items-center justify-center gap-3">
                            <AlertCircle className="w-8 h-8 text-red-400" />
                            The Current Bottleneck
                        </h2>
                    </div>
                    
                    <Card className="max-w-4xl mx-auto border-red-500/20 bg-red-500/5">
                        <ul className="space-y-6">
                            <li className="flex gap-4 items-start">
                                <span className="bg-red-500/20 text-red-400 p-2 rounded-xl flex-shrink-0 font-bold">1</span>
                                <span className="text-white/80 leading-relaxed text-lg pt-1">
                                    <strong className="text-white">Audience Mismatch:</strong> Your current footprint strongly attracts young freelancers, but your immediate goal is to command authority and capture high-value Founders.
                                </span>
                            </li>
                            <li className="flex gap-4 items-start">
                                <span className="bg-red-500/20 text-red-400 p-2 rounded-xl flex-shrink-0 font-bold">2</span>
                                <span className="text-white/80 leading-relaxed text-lg pt-1">
                                    <strong className="text-white">Broken Lead Flow & Funnel:</strong> The problem isn't just "content." It's that top-of-funnel views are not effectively translating down into your Gumroad and conversion funnels. The backend pipeline is leaking attention.
                                </span>
                            </li>
                            <li className="flex gap-4 items-start">
                                <span className="bg-red-500/20 text-red-400 p-2 rounded-xl flex-shrink-0 font-bold">3</span>
                                <span className="text-white/80 leading-relaxed text-lg pt-1">
                                    <strong className="text-white">Ad-Hoc Execution:</strong> You lack absolute clarity on what precise content to make next. Every post feels like starting from scratch because the operational system is missing.
                                </span>
                            </li>
                        </ul>
                    </Card>
                </section>

                {/* The Solution */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400 mb-4 flex items-center justify-center gap-3">
                            <Target className="w-8 h-8 text-emerald-400" />
                            Our Solution Architecture
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <Card className="border-emerald-500/20 bg-emerald-500/5">
                            <Filter className="w-10 h-10 text-emerald-400 mb-4" />
                            <h3 className="text-2xl font-bold mb-3 text-white">Full Funnel Realignment</h3>
                            <p className="text-white/70 text-lg">
                                We go beyond just building videos. We audit your Gumroad links, calls-to-action, and bio structure to ensure every view is routed into a high-converting lead capture workflow.
                            </p>
                        </Card>

                        <Card className="border-emerald-500/20 bg-emerald-500/5">
                            <Compass className="w-10 h-10 text-emerald-400 mb-4" />
                            <h3 className="text-2xl font-bold mb-3 text-white">Founder Positioning</h3>
                            <p className="text-white/70 text-lg">
                                We permanently shift your messaging from "how to write" to "how to scale." We use the SPG framework to build narratives that exclusively attract business owners.
                            </p>
                        </Card>

                        <Card className="border-emerald-500/20 bg-emerald-500/5">
                            <Database className="w-10 h-10 text-emerald-400 mb-4" />
                            <h3 className="text-2xl font-bold mb-3 text-white">The Centralized OS</h3>
                            <p className="text-white/70 text-lg">
                                We eliminate the chaos of planning. We install a robust workspace that houses your ideas, tracks script statuses, and gives you a single source of truth for production.
                            </p>
                        </Card>
                    </div>
                </section>

                {/* Detailed Steps */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            The Complete Deliverable Stack
                        </h2>
                        <p className="text-slate-400 text-lg max-w-2xl mx-auto">This is an intricate ecosystem build, not just a video editing package.</p>
                    </div>

                    <div className="space-y-8 max-w-5xl mx-auto">
                        <Card className="hover:border-indigo-500/50 transition-colors duration-300">
                            <h3 className="text-2xl font-bold text-indigo-300 mb-4 flex items-center gap-3">
                                <Workflow className="w-8 h-8" /> 1. Consulting and Lead-Flow Engineering
                            </h3>
                            <div className="grid md:grid-cols-2 gap-6 mt-6">
                                <ul className="space-y-4">
                                    <CheckRow>Deep-dive funnel audit of your existing Gumroad architecture and lead capture points.</CheckRow>
                                    <CheckRow>Complete recalibration of your hooks and Call-To-Actions to maximize newsletter/product signups.</CheckRow>
                                </ul>
                                <ul className="space-y-4">
                                    <CheckRow>Strategy sessions identifying exactly what topics pull in high-ticket founders.</CheckRow>
                                    <CheckRow>Mapping out your core narrative pillars so you never second-guess your content direction.</CheckRow>
                                </ul>
                            </div>
                        </Card>

                        <Card className="hover:border-indigo-500/50 transition-colors duration-300">
                            <h3 className="text-2xl font-bold text-indigo-300 mb-4 flex items-center gap-3">
                                <Database className="w-8 h-8" /> 2. The Growth Partner OS Installation
                            </h3>
                            <div className="grid md:grid-cols-2 gap-6 mt-6">
                                <ul className="space-y-4">
                                    <CheckRow>Integration of our proprietary Content OS directly into your Notion workspace.</CheckRow>
                                    <CheckRow>Pre-loaded SPG (Support, Point, Guide) phrasing templates specifically designed for your tone.</CheckRow>
                                </ul>
                                <ul className="space-y-4">
                                    <CheckRow>A fully operational pipeline moving ideas from 'Raw Thought' out to 'Published & Indexed'.</CheckRow>
                                    <CheckRow>Say goodbye to blank-page syndrome. The system dictates what gets written.</CheckRow>
                                </ul>
                            </div>
                        </Card>

                        <Card className="hover:border-indigo-500/50 transition-colors duration-300">
                            <h3 className="text-2xl font-bold text-indigo-300 mb-4 flex items-center gap-3">
                                <LineChart className="w-8 h-8" /> 3. End-to-End Visual Execution
                            </h3>
                            <div className="grid md:grid-cols-2 gap-6 mt-6">
                                <ul className="space-y-4">
                                    <CheckRow>Premium, retention-focused video editing designed specifically for high-end B2B and LinkedIn aesthetics.</CheckRow>
                                    <CheckRow>Professional script auditing ensuring every video hits the crucial 3-second hook threshold.</CheckRow>
                                </ul>
                                <ul className="space-y-4">
                                    <CheckRow>Total asset management and final multi-platform packaging.</CheckRow>
                                    <CheckRow>You turn on the camera to speak. The machine handles the rest.</CheckRow>
                                </ul>
                            </div>
                        </Card>
                    </div>
                </section>

                {/* Investment Tiers */}
                <section id="investment" className="pb-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">Investment Tiers</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        
                        <Card className="flex flex-col border-t-4 border-t-slate-400 group relative">
                            <div className="mb-6 border-b border-white/10 pb-6 text-center">
                                <h3 className="text-2xl font-bold mb-2">Done With You</h3>
                                <div className="mt-4">
                                    <span className="text-4xl font-bold text-white">$799</span>
                                    <span className="text-white/60 font-medium"> / mo</span>
                                </div>
                            </div>
                            <div className="flex-grow">
                                <ul className="space-y-4 mb-8">
                                    <CheckRow>Initial Funnel & Gumroad Flow Audit</CheckRow>
                                    <CheckRow>Notion Content OS Custom Installation</CheckRow>
                                    <CheckRow>SPG Hook Frameworks Access</CheckRow>
                                    <CheckRow>2x Monthly Brainstorming & Alignment Calls</CheckRow>
                                    <CheckRow>Async structural feedback on 4 core scripts</CheckRow>
                                </ul>
                            </div>
                        </Card>

                        <Card className="flex flex-col border-t-4 border-t-indigo-500 bg-indigo-500/10 transform md:-translate-y-4 shadow-[0_0_50px_-15px_rgba(99,102,241,0.3)]">
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 justify-center bg-indigo-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest shadow-lg flex items-center gap-1">
                                Complete Engine <ArrowRight className="w-3 h-3" />
                            </div>
                            <div className="mb-6 border-b border-white/10 pb-6 text-center mt-2">
                                <h3 className="text-2xl font-bold mb-2">Done For You</h3>
                                <div className="mt-4">
                                    <span className="text-4xl font-bold text-white">$1,499</span>
                                    <span className="text-white/60 font-medium"> / mo</span>
                                </div>
                            </div>
                            <div className="flex-grow">
                                <ul className="space-y-4 mb-8">
                                    <CheckRow>Everything in the Done With You Tier</CheckRow>
                                    <CheckRow>Complete End-to-End Scriptwriting</CheckRow>
                                    <CheckRow>Premium Executive-Tier Video Production</CheckRow>
                                    <CheckRow>Multi-Platform Asset Formatting</CheckRow>
                                    <CheckRow>Hands-Off Operational Pipeline—You Just Record</CheckRow>
                                </ul>
                            </div>
                        </Card>

                    </div>
                </section>

            </main>
        </div>
    );
}
